/**
 * Phase 8.3 — Cross-Jurisdiction Routing layer (CD R9 Tier-A #3).
 *
 * "Who's affected, why, told?" card. Auto-routes a signal to the
 * jurisdictions it materially affects, with the REASON taxonomy
 * (border / travel / genomic / mandate), per-jurisdiction focal point,
 * and ack-tracking. Used as a LAYER (not a §7.x surface) per CD's
 * rule "new view of an existing noun → new mode" — embed wherever a
 * signal is the noun on the page (PerPathogenDrilldown today;
 * OperatorWorkspace's pinned-signal panel next).
 *
 * Honesty rails (CD R9 spec):
 *   1. Suggested-not-auto-send — the system PROPOSES affected jurisdictions
 *      with the reason; a human confirms before any notification leaves
 *      the building.
 *   2. Ack-tracking so nothing is assumed-delivered. Pending stays visible
 *      until the focal-point acknowledges.
 *   3. No editorial line on any jurisdiction — we don't claim "should have
 *      done X better"; we just route the signal + log who saw it when.
 *
 * Sample data is illustrative — the live routing pipeline (genomic +
 * mobility + trade flows) is the Tier-S build behind this surface.
 */

import { useState } from 'react';
import { auditCapture } from '../audit/capture';

export type RoutingReason = 'border' | 'travel' | 'genomic' | 'mandate';

const REASON_META: Record<RoutingReason, { cssVar: string; icon: string; word: string; detail: string }> = {
  border:  { cssVar: 'var(--bm-threat-2)',        icon: '▣', word: 'Shared border',  detail: 'Jurisdiction shares a land border with the signal origin; cross-border seeding plausible within days.' },
  travel:  { cssVar: 'var(--bm-accent, #6ea3ff)', icon: '✈', word: 'Travel-linked',  detail: 'Significant passenger / commerce flow from signal origin; importation risk via airports or land transit hubs.' },
  genomic: { cssVar: 'var(--bm-prov-synthetic, #b06)', icon: '⬡', word: 'Genomic link', detail: 'A sequenced case in this jurisdiction is genomically linked to the signal origin cluster.' },
  mandate: { cssVar: 'var(--bm-accent, #6ea3ff)', icon: '◎', word: 'Mandate',        detail: 'Regional / multilateral body with coordination responsibility (e.g. WHO Regional Office, ECOWAS health pillar).' },
};

export type AckStatus = 'acked' | 'pending';

export interface RoutingTarget {
  jurisdiction: string;
  reason: RoutingReason;
  detail: string;
  focal: string;
  ack: AckStatus;
  ackTimeAgo?: string;
}

export interface RoutingOrigin {
  pathogenName: string;
  pathogenIcon?: string;
  pathogenColor?: string;
  location: string;
  severity?: 'CRITICAL' | 'HIGH' | 'ELEVATED' | 'WATCH';
}

interface CrossJurisdictionRoutingProps {
  origin: RoutingOrigin;
  targets: readonly RoutingTarget[];
  surface: string;
}

export function CrossJurisdictionRouting({ origin, targets, surface }: CrossJurisdictionRoutingProps) {
  const [sel, setSel] = useState<string | null>(null);
  const acked = targets.filter(t => t.ack === 'acked').length;

  const handleSelect = (jur: string) => {
    const next = sel === jur ? null : jur;
    setSel(next);
    if (next) {
      auditCapture.emit({
        event: { type: 'surface.view', surface, subjectIds: [], payload: { routingExpanded: next } },
      });
    }
  };

  return (
    <div style={{
      background: 'var(--bm-bg-panel, var(--bm-bg))',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-md)',
      padding: 18,
    }}>
      <header style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 650 }}>
          <span style={{ color: origin.pathogenColor ?? 'var(--bm-threat-1)' }}>
            {origin.pathogenIcon ?? '▲'}
          </span>
          {' '}{origin.pathogenName} · {origin.location}
        </div>
        <div style={{
          marginTop: 3,
          fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, color: 'var(--bm-text-subtle)',
        }}>
          {origin.severity ?? 'WATCH'} · routed to {targets.length} jurisdiction{targets.length === 1 ? '' : 's'} ·
          {' '}{acked}/{targets.length} acknowledged
        </div>
      </header>

      <div style={{
        display: 'grid', gridTemplateColumns: '64px 1fr', gap: 16, alignItems: 'center',
      }}>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
          textAlign: 'center' as const, color: 'var(--bm-text-muted)',
          background: 'var(--bm-bg-subtle)',
          border: '1px solid var(--bm-border)',
          borderRadius: 10,
          padding: '14px 6px', lineHeight: 1.3,
        }}>SIGNAL<br />ORIGIN</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {targets.map(t => {
            const meta = REASON_META[t.reason];
            const isSel = sel === t.jurisdiction;
            return (
              <button
                key={t.jurisdiction}
                type="button"
                onClick={() => handleSelect(t.jurisdiction)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left' as const,
                  background: 'var(--bm-bg-subtle)',
                  border: `1px solid ${isSel ? 'var(--bm-text-muted)' : 'var(--bm-border)'}`,
                  borderLeft: `3px solid ${meta.cssVar}`,
                  borderRadius: 8, padding: '9px 12px', cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, flex: 1, color: 'var(--bm-text)' }}>
                    {t.jurisdiction}
                  </span>
                  <span style={{
                    fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
                    color: meta.cssVar,
                    border: `1px solid ${meta.cssVar}66`,
                    borderRadius: 5, padding: '1px 7px',
                  }}>{meta.icon} {meta.word}</span>
                  <span style={{
                    fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
                    color: t.ack === 'acked' ? 'var(--bm-prov-confirmed, var(--bm-state-live))' : 'var(--bm-text-subtle)',
                  }}>
                    {t.ack === 'acked' ? `✓ ack ${t.ackTimeAgo ?? ''}` : '◷ pending'}
                  </span>
                </div>
                {isSel && (
                  <div style={{
                    marginTop: 7, paddingTop: 7,
                    borderTop: '1px solid var(--bm-border)',
                    fontFamily: 'var(--bm-font-mono)', fontSize: 11,
                    color: 'var(--bm-text-muted)',
                  }}>
                    {t.detail} · focal point: <b style={{ color: 'var(--bm-text)' }}>{t.focal}</b>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{
        marginTop: 14, padding: '10px 13px',
        fontSize: 12.5, color: 'var(--bm-text-muted)', lineHeight: 1.5,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderLeft: '3px solid var(--bm-prov-confirmed, var(--bm-state-live))',
        borderRadius: 7,
      }}>
        <b style={{ color: 'var(--bm-text)' }}>Routing is suggested, not automatic-send.</b>
        {' '}The system proposes affected jurisdictions + the reason; a human confirms before
        notification leaves the building. Ack status is tracked so nothing is assumed-delivered.
        No editorial line on any jurisdiction.
      </div>
    </div>
  );
}

/** Illustrative sample data — Ebola Zaïre cluster routing example from
 *  CD R9. Real routing pipeline (genomic + mobility + trade flows) lands
 *  in Phase 9 / Tier-S build. Surfaces consume this when no live routing
 *  state is available. */
export const SAMPLE_ROUTING_TARGETS: readonly RoutingTarget[] = [
  { jurisdiction: 'Uganda',   reason: 'border',  detail: 'shared border, 47km from cluster',                focal: 'Kampala EOC',    ack: 'acked',   ackTimeAgo: '12m' },
  { jurisdiction: 'Rwanda',   reason: 'travel',  detail: 'direct flights + cross-border trade',             focal: 'Kigali NRL',     ack: 'acked',   ackTimeAgo: '31m' },
  { jurisdiction: 'Kenya',    reason: 'travel',  detail: 'regional transit hub (NBO)',                      focal: 'Nairobi FELTP',  ack: 'pending' },
  { jurisdiction: 'Belgium',  reason: 'genomic', detail: 'genomically-linked case, 1 sequence',             focal: 'Sciensano',      ack: 'pending' },
  { jurisdiction: 'WHO AFRO', reason: 'mandate', detail: 'regional coordination',                           focal: 'Brazzaville RHE', ack: 'acked', ackTimeAgo: '4m' },
];

export const SAMPLE_ROUTING_ORIGIN: RoutingOrigin = {
  pathogenName: 'Ebola (Zaïre)',
  pathogenIcon: '▲',
  pathogenColor: 'var(--bm-id-7, var(--bm-threat-1))',
  location: 'North Kivu, DR Congo',
  severity: 'CRITICAL',
};
